import dayjs from 'dayjs';

/**
 * Helpers for profile pages that save one tile at a time from a single form:
 * compare the form with the values it was loaded with, and build a request
 * body that carries only what changed.
 */

// Comparable text of a form value: dates by day, keys sorted, empty = absent.
export const stableStringify = (value) =>
  JSON.stringify(value, function replacer(key, v) {
    const raw = this[key];
    if (dayjs.isDayjs(raw)) return raw.format('YYYY-MM-DD');
    if (v === undefined || v === null || v === '') return undefined;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return Object.fromEntries(
        Object.keys(v)
          .sort()
          .map((k) => [k, v[k]])
      );
    }
    return v;
  }) ?? '';

export const isEmptyValue = (value) =>
  ['', '{}', '[]'].includes(stableStringify(value));

/** `changed(key)`: whether a top-level form key differs from the loaded value. */
export const makeChanged = (current, base) => (key) =>
  stableStringify(current?.[key]) !== stableStringify(base?.[key]);

/**
 * A group the server replaces wholesale ($set of the whole object) is sent as
 * stored value + form values, so keys the form does not render are kept. Keys
 * empty both as loaded and in the form stay out: responses pad some missing
 * keys with "" and forms add empty defaults.
 */
export const mergeChangedGroup = (stored = {}, sent = {}) =>
  Object.fromEntries(
    Object.entries({ ...stored, ...sent }).filter(
      ([key, value]) => !isEmptyValue(value) || !isEmptyValue(stored?.[key])
    )
  );

const isPlainGroup = (v) =>
  v && typeof v === 'object' && !Array.isArray(v) && !dayjs.isDayjs(v);

/**
 * Tiles whose fields differ from the loaded values.
 *
 * @param {Object} current - form.getFieldsValue(true)
 * @param {Object} base - the values as loaded
 * @param {(name: string[]) => string} sectionOfField - field path → tile id
 * @returns {string[]} Sorted tile ids
 */
export const dirtySections = (current, base, sectionOfField) => {
  const dirty = new Set();
  new Set([...Object.keys(current || {}), ...Object.keys(base || {})]).forEach(
    (key) => {
      const cur = current?.[key];
      const old = base?.[key];
      if (stableStringify(cur) === stableStringify(old)) return;
      if (!isPlainGroup(cur) && !isPlainGroup(old)) {
        dirty.add(sectionOfField([key]));
        return;
      }
      new Set([...Object.keys(cur || {}), ...Object.keys(old || {})]).forEach(
        (sub) => {
          if (stableStringify(cur?.[sub]) !== stableStringify(old?.[sub])) {
            dirty.add(sectionOfField([key, sub]));
          }
        }
      );
    }
  );
  return [...dirty].sort();
};

/**
 * The form values with every field outside one tile put back as loaded, so a
 * save sends that tile's changes only. Keys follow `current`, like the values
 * the form submits.
 *
 * @param {Object} current - form values
 * @param {Object|null} base - the values as loaded
 * @param {(name: string[]) => string} sectionOfField - field path → tile id
 * @param {string} section - the tile being saved
 * @returns {Object}
 */
export const scopeToSection = (current, base, sectionOfField, section) => {
  if (!base) return current;
  const pick = (name, cur, old) =>
    sectionOfField(name) === section ? cur : old;
  return Object.fromEntries(
    Object.keys(current || {}).map((key) => {
      const cur = current[key];
      const old = base[key];
      if (!isPlainGroup(cur) && !isPlainGroup(old)) {
        return [key, pick([key], cur, old)];
      }
      const subs = new Set([
        ...Object.keys(isPlainGroup(cur) ? cur : {}),
        ...Object.keys(isPlainGroup(old) ? old : {}),
      ]);
      return [
        key,
        Object.fromEntries(
          [...subs].map((sub) => [
            sub,
            pick([key, sub], cur?.[sub], old?.[sub]),
          ])
        ),
      ];
    })
  );
};
